import { useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Calendar, Clock, TrendingUp, ArrowLeft, Share2, Bookmark, ChevronUp } from "lucide-react";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import SocialShareButtons from "@/components/SocialShareButtons";
import OpenGraphMeta from "@/components/OpenGraphMeta";
import ArticleCtaBar from "@/components/article/ArticleCtaBar";
import InlineCoinAnalysisCard from "@/components/article/InlineCoinAnalysisCard";
import ArticleFooterCTA from "@/components/article/ArticleFooterCTA";
import ArticleNav from "@/components/article/ArticleNav";
import { useScrollDepth } from "@/hooks/useScrollDepth";
import { trackEvent } from "@/lib/trackEvent";

// Preprocess markdown to fix malformed tables (missing line breaks)
const preprocessMarkdown = (content: string): string => {
  // Work block-by-block so we only touch sections that look like markdown tables.
  const blocks = content.split(/\n{2,}/g);

  const fixedBlocks = blocks.map((block) => {
    const looksLikeTable = /\|\s*[-:]{3,}\s*\|/.test(block);
    if (!looksLikeTable) return block;

    // If a generated table is clumped on one line, rows are often separated like: "| value | | nextRow ..."
    // Convert whitespace-only separators between pipes into a newline row break.
    return block.replace(/\|\s*\|/g, "|\n|");
  });

  return fixedBlocks.join("\n\n");
};

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('utm_source') || 'direct';
  const [showScrollTop, setShowScrollTop] = useState(false);

  const { data: article, isLoading } = useQuery({
    queryKey: ['article', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!slug
  });

  // Sibling articles (for related / prev / next)
  const { data: siblings = [] } = useQuery({
    queryKey: ['article-siblings', article?.category, article?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('id, slug, headline, category, og_image_url, publish_date')
        .eq('status', 'published')
        .order('publish_date', { ascending: false })
        .limit(60);
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!article?.id,
  });

  const mentionedCoins: string[] = (article?.tokens_featured as string[] | null) ?? [];
  const primaryCoin: string | null =
    (article?.primary_token as string | null) || mentionedCoins[0] || null;

  const currentIndex = siblings.findIndex((s) => s.id === article?.id);
  const previousArticle = currentIndex > 0 ? siblings[currentIndex - 1] : null;
  const nextArticle =
    currentIndex >= 0 && currentIndex < siblings.length - 1 ? siblings[currentIndex + 1] : null;
  const relatedArticles = siblings
    .filter((s) => s.id !== article?.id)
    .sort((a, b) => {
      const aMatch = a.category === article?.category ? 0 : 1;
      const bMatch = b.category === article?.category ? 0 : 1;
      return aMatch - bMatch;
    })
    .slice(0, 3);

  // Engagement tracking
  useScrollDepth(!!article?.id, { article_id: article?.id, slug });

  useEffect(() => {
    if (!article?.id) return;
    trackEvent('article_view', {
      article_id: article.id,
      slug,
      category: article.category,
      primary_coin: primaryCoin,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article?.id]);



  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track view count (once per session per article)
  useEffect(() => {
    const trackView = async () => {
      if (!article?.id) return;
      
      const sessionKey = `viewed_${article.id}`;
      if (sessionStorage.getItem(sessionKey)) return;
      
      try {
        // Use RPC or direct update - we'll use direct update with current count
        const currentCount = article.view_count || 0;
        await supabase
          .from('articles')
          .update({ view_count: currentCount + 1 })
          .eq('id', article.id);
        
        sessionStorage.setItem(sessionKey, 'true');
      } catch (error) {
        console.error('Failed to track view:', error);
      }
    };
    
    trackView();
  }, [article?.id, article?.view_count]);

  useEffect(() => {
    if (article?.id && article.content) {
      // Track page view
      supabase.functions.invoke('track-analytics', {
        body: {
          article_id: article.id,
          event_type: 'view',
          source: source
        }
      }).catch(console.error);

      // Add click tracking to affiliate links
      const timer = setTimeout(() => {
        const links = document.querySelectorAll('a[href*="coinspot"], a[href*="swyftx"]');
        
        links.forEach(link => {
          link.addEventListener('click', async (e) => {
            const href = (e.currentTarget as HTMLAnchorElement).href;
            const platform = href.includes('coinspot') ? 'coinspot' : 'swyftx';
            await handleAffiliateClick(platform);
          });
        });
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [article?.id, article?.content, source]);

  const handleAffiliateClick = async (platform: string) => {
    if (article?.id) {
      await supabase.functions.invoke('track-analytics', {
        body: {
          article_id: article.id,
          event_type: 'affiliate_click',
          platform: platform
        }
      }).catch(console.error);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          <div className="animate-pulse space-y-8">
            <div className="h-64 bg-muted rounded-2xl" />
            <div className="space-y-4">
              <div className="h-10 bg-muted rounded w-3/4" />
              <div className="h-6 bg-muted rounded w-1/2" />
            </div>
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-4 bg-muted rounded w-full" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-12 text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
            <TrendingUp className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-bold mb-3">Article Not Found</h2>
          <p className="text-muted-foreground mb-6">The article you're looking for doesn't exist or has been removed.</p>
          <Link to="/">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  const articleUrl = `/articles/${slug}`;
  const description = article?.summary || article?.seo_description || article?.deck || '';
  const hashtags = article?.tokens_featured?.map(token => token.replace(/[^a-zA-Z0-9]/g, '')) || ['crypto', 'aussiecrypto'];
  const readingTime = article?.reading_time || Math.ceil((article?.word_count || 500) / 200);

  // Default placeholder image if no og_image_url
  const heroImage = (article as any)?.og_image_url || `https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=1200&h=630&fit=crop&auto=format`;

  // Split content so an inline CTA card can sit after the first ~3 blocks
  const processedContent = preprocessMarkdown(article.content);
  const blocks = processedContent.split(/\n{2,}/g);
  const splitAt = Math.min(3, Math.max(1, blocks.length - 1));
  const contentIntro = blocks.slice(0, splitAt).join('\n\n');
  const contentRest = blocks.slice(splitAt).join('\n\n');

  const mdComponents = {
    a: ({ node, ...props }: any) => {
      const href = props.href || '';
      const isCoinSpot = href.includes('coinspot.com.au');
      const isSwyftx = href.includes('swyftx.com');
      return (
        <a
          {...props}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline font-medium"
          onClick={() => {
            if (isCoinSpot) handleAffiliateClick('coinspot');
            if (isSwyftx) handleAffiliateClick('swyftx');
          }}
        />
      );
    },
    h2: ({ children }: any) => (
      <h2 className="text-2xl sm:text-3xl font-bold mt-12 mb-4 text-foreground scroll-mt-20">{children}</h2>
    ),
    h3: ({ children }: any) => (
      <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3 text-foreground">{children}</h3>
    ),
    p: ({ children }: any) => (
      <p className="text-[1.0625rem] sm:text-lg text-foreground/90 leading-8 mb-6">{children}</p>
    ),
    ul: ({ children }: any) => (
      <ul className="my-6 ml-6 space-y-3 list-disc marker:text-primary">{children}</ul>
    ),
    ol: ({ children }: any) => (
      <ol className="my-6 ml-6 space-y-3 list-decimal marker:text-primary">{children}</ol>
    ),
    li: ({ children }: any) => (
      <li className="text-[1.0625rem] sm:text-lg text-foreground/90 leading-8 pl-2">{children}</li>
    ),
    blockquote: ({ children }: any) => (
      <blockquote className="border-l-4 border-primary/50 pl-6 my-8 italic text-muted-foreground bg-muted/30 py-4 pr-4 rounded-r-lg">
        {children}
      </blockquote>
    ),
    table: ({ children }: any) => (
      <div className="my-8 overflow-x-auto rounded-xl border border-border/60 bg-card/30">
        <table className="w-full min-w-[480px] border-separate border-spacing-0 text-sm">{children}</table>
      </div>
    ),
    thead: ({ children }: any) => <thead className="bg-muted/50">{children}</thead>,
    tbody: ({ children }: any) => <tbody className="divide-y divide-border/30">{children}</tbody>,
    tr: ({ children }: any) => <tr className="hover:bg-muted/30 transition-colors">{children}</tr>,
    th: ({ children }: any) => (
      <th className="px-4 py-3 text-left font-semibold text-foreground border-b border-border/50">{children}</th>
    ),
    td: ({ children }: any) => <td className="px-4 py-3 text-foreground/90">{children}</td>,
  };

  return (
    <>
      <OpenGraphMeta
        title={article?.seo_title || article?.headline || 'Crypto Analysis'}
        description={description}
        url={articleUrl}
        image={heroImage}
        type="article"
        publishedTime={article?.publish_date || undefined}
        tags={article?.seo_keywords || []}
      />

      <div className="min-h-screen bg-background">
        {/* Sticky Header */}
        <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Analysis</span>
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <SocialShareButtons
                url={window.location.href}
                title={article.headline}
                description={description}
                hashtags={hashtags}
                showLabel={false}
                size="sm"
                articleId={article.id}
              />
            </div>
          </div>
        </header>

        {/* Hero Section with Image */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background z-10" />
          <div 
            className="w-full h-[300px] sm:h-[400px] md:h-[450px] bg-cover bg-center"
            style={{ backgroundImage: `url(${heroImage})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent z-10" />
        </div>

        {/* Article Content */}
        <main className="container mx-auto px-4 -mt-32 relative z-20">
          <article className="max-w-3xl mx-auto">
            {/* Article Header */}
            <div className="mb-10">
              {/* Category & Tokens */}
              <div className="flex flex-wrap items-center gap-2 mb-5">
                {article.featured && (
                  <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                    Featured
                  </Badge>
                )}
                <Badge variant="outline" className="border-primary/50 text-primary">
                  {article.category}
                </Badge>
                {article.tokens_featured?.slice(0, 3).map((token) => (
                  <Badge key={token} variant="secondary" className="gap-1">
                    <TrendingUp className="w-3 h-3" />
                    {token}
                  </Badge>
                ))}
              </div>

              {/* Headline */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-5 leading-tight tracking-tight">
                {article.headline}
              </h1>

              {/* Deck/Subtitle */}
              {article.deck && (
                <p className="text-xl text-muted-foreground leading-relaxed mb-6">
                  {article.deck}
                </p>
              )}

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-muted-foreground pb-6 border-b border-border/50">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <time dateTime={article.publish_date || ''}>
                    {format(new Date(article.publish_date || new Date()), 'MMMM d, yyyy')}
                  </time>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{readingTime} min read</span>
                </div>
                {article.opportunity_score && (
                  <div className="flex items-center gap-2 text-primary font-medium">
                    <TrendingUp className="h-4 w-4" />
                    <span>Score: {article.opportunity_score}/100</span>
                  </div>
                )}
              </div>
            </div>

            {/* Top conversion bar */}
            <ArticleCtaBar coin={primaryCoin} articleId={article.id} slug={slug} />

            {/* Main Content */}
            <div className="prose prose-lg max-w-none 
              prose-headings:font-bold prose-headings:tracking-tight
              prose-h1:text-3xl prose-h1:mt-12 prose-h1:mb-6
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-foreground/90 prose-p:leading-relaxed prose-p:mb-6
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              prose-strong:text-foreground prose-strong:font-semibold
              prose-ul:my-6 prose-ul:space-y-2
              prose-ol:my-6 prose-ol:space-y-2
              prose-li:text-foreground/90 prose-li:leading-relaxed
              prose-blockquote:border-l-4 prose-blockquote:border-primary/50 
              prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-muted-foreground
              prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
              prose-img:rounded-xl prose-img:shadow-lg
            ">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {contentIntro}
              </ReactMarkdown>

              <InlineCoinAnalysisCard
                coin={primaryCoin}
                articleId={article.id}
                slug={slug}
              />

              {contentRest && (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                  {contentRest}
                </ReactMarkdown>
              )}
            </div>

            {/* AI Recommendation Card */}
            {article.ai_recommendation && (
              <Card className="p-6 sm:p-8 my-10 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-primary/20">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">AI Analysis Summary</h3>
                    <p className="text-muted-foreground leading-relaxed">{article.ai_recommendation}</p>
                  </div>
                </div>
              </Card>
            )}

            {/* Share Section */}
            <div className="py-10 border-t border-border/50">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold mb-2">Found this analysis helpful?</h3>
                <p className="text-muted-foreground">Share it with your crypto community</p>
              </div>
              <div className="flex justify-center">
                <SocialShareButtons
                  url={window.location.href}
                  title={article.headline}
                  description={description}
                  hashtags={hashtags}
                  showLabel={true}
                  size="lg"
                  articleId={article.id}
                />
              </div>
            </div>

            {/* Keywords */}
            {article.seo_keywords && article.seo_keywords.length > 0 && (
              <div className="pb-10">
                <div className="flex flex-wrap gap-2 justify-center">
                  {article.seo_keywords.map((keyword: string, idx: number) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {keyword}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Prev / Next navigation */}
            <ArticleNav
              previous={previousArticle}
              next={nextArticle}
              articleId={article.id}
              slug={slug}
            />

            {/* Closing conversion section */}
            <ArticleFooterCTA
              related={relatedArticles}
              coins={mentionedCoins}
              articleId={article.id}
              slug={slug}
            />
          </article>
        </main>

        {/* Floating Share Button */}

        {/* Scroll to Top Button */}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-6 left-6 z-50 w-12 h-12 rounded-full bg-muted hover:bg-muted/80 shadow-lg flex items-center justify-center transition-all hover:scale-110"
            aria-label="Scroll to top"
          >
            <ChevronUp className="w-5 h-5" />
          </button>
        )}
      </div>
    </>
  );
}
